import { ApiResponse, FetchOptions, useFetch } from '@/api/useFetch';
import CONSTANTS from '@/helpers/constants.ts';
import { getCookie } from '@/helpers/utils';
import {
  GetAccountAccessesResponse,
  GetAccountsResponse,
  GetPasswordTermsResponse,
  ImpersonateResponse,
} from '@/pages/Accounts/Accounts.types.ts';
import type {
  ForgetPasswordMobileVerification,
  ForgetPasswordMobileVerificationPayload,
  ForgetPasswordPayload,
} from '@/pages/ForgetPassword/ForgetPassword.types';
import type { OtpSignInResponse, SignInPayload, SignInResponse } from '@/pages/SignIn/SignIn.types';
import type {
  SignUp,
  SignUpMobileVerification,
  SignUpMobileVerificationPayload,
  SignUpPayload,
} from '@/pages/SignUp/SignUp.types';
import { VerifyUserPayload, VerifyUserResponse } from '@/pages/VerifyUser/VerifyUser.types';
import { ApiMethod } from '@/types/enums';
import { AuthApiOkResponse, RecaptchaInfoResponse } from '@/types/shared';

const { AUTH, SKYROOM } = CONSTANTS.API_ENTITY_URLS;

interface CsrfTokenData {
  csrf_token: string;
}

export const useCsrfToken = async (): Promise<ApiResponse<{ ok: boolean; data: CsrfTokenData }>> => {
  return await useFetch('auth/api/csrf-token', {
    method: ApiMethod.Get,
    noToast: true,
  });
};

const useFetchWithCSRF = async <T>(url: string, options: FetchOptions): Promise<ApiResponse<T>> => {
  let csrfToken = undefined;
  const csrfCookie = getCookie('XSRF_TOKEN');

  if (csrfCookie) {
    csrfToken = csrfCookie;
  } else {
    const csrfResponse = await useCsrfToken();

    if (csrfResponse.data?.data.csrf_token) {
      csrfToken = csrfResponse.data.data.csrf_token;
    }
  }

  return await useFetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
  });
};

export const useSignIn = async (body: SignInPayload): Promise<ApiResponse<SignInResponse>> => {
  return await useFetchWithCSRF('auth/api/login', {
    method: ApiMethod.Post,
    body: JSON.stringify(body),
  });
};

export const useSignOut = async (): Promise<ApiResponse<AuthApiOkResponse>> => {
  return await useFetch('auth/api/logout', {
    method: ApiMethod.Post,
  });
};

export const useMobileVerification = async (
  body: SignUpMobileVerificationPayload,
  options?: Pick<FetchOptions, 'noToast'>,
): Promise<ApiResponse<SignUpMobileVerification>> => {
  return await useFetchWithCSRF('auth/api/authenticate', {
    method: ApiMethod.Post,
    body: JSON.stringify(body),
    noToast: options?.noToast,
  });
};

export const useSignUp = async (body: SignUpPayload): Promise<ApiResponse<SignUp>> => {
  return await useFetchWithCSRF('auth/api/register', {
    method: ApiMethod.Post,
    body: JSON.stringify(body),
  });
};

export const useForgetPasswordMobileVerification = async (
  body: ForgetPasswordMobileVerificationPayload,
): Promise<ApiResponse<ForgetPasswordMobileVerification>> => {
  return await useFetchWithCSRF('auth/api/password-recovery/authenticate', {
    method: ApiMethod.Post,
    body: JSON.stringify(body),
  });
};

export const useForgetPassword = async (body: ForgetPasswordPayload): Promise<ApiResponse<AuthApiOkResponse>> => {
  return await useFetchWithCSRF('auth/api/password-recovery', {
    method: ApiMethod.Post,
    body: JSON.stringify(body),
  });
};

export const useRecaptchaInfo = async (): Promise<ApiResponse<RecaptchaInfoResponse>> => {
  return await useFetch('api/jaws/recaptchaInfo', {
    method: ApiMethod.Get,
  });
};

export const useAccountAccesses = async (): Promise<ApiResponse<GetAccountAccessesResponse>> => {
  return await useFetch(`api/${AUTH}/accesses`, {
    method: ApiMethod.Get,
    noToast: true,
  });
};

export const useUserAccountsInfo = async (): Promise<ApiResponse<GetAccountsResponse>> => {
  return await useFetch(`api/${AUTH}/accounts`, {
    method: ApiMethod.Get,
  });
};

export const useUserImpersonate = async (id: number): Promise<ApiResponse<ImpersonateResponse>> => {
  return await useFetch(`api/${AUTH}/${id}/impersonate`, {
    method: ApiMethod.Get,
  });
};

export const useVerifyUser = async (body: VerifyUserPayload): Promise<ApiResponse<VerifyUserResponse>> => {
  return await useFetch(`auth/api/mobile-verify`, {
    method: ApiMethod.Post,
    body: JSON.stringify(body),
  });
};

export const usePasswordTerms = async (): Promise<ApiResponse<GetPasswordTermsResponse>> => {
  return await useFetch(`${AUTH}/api/password-terms`, {
    method: ApiMethod.Get,
  });
};

export const useSignInOTP = async (
  body: ForgetPasswordMobileVerificationPayload,
): Promise<ApiResponse<OtpSignInResponse>> => {
  return await useFetchWithCSRF(`${AUTH}/api/login/otp`, {
    method: ApiMethod.Post,
    body: JSON.stringify(body),
  });
};

export const useCheckSignInOTP = async (): Promise<ApiResponse<{ ok: boolean; result: boolean }>> => {
  return await useFetch(`${SKYROOM}/check/otp-login`, {
    method: ApiMethod.Get,
  });
};
